import { ShoppingCart, Check, Plus, Scale, Sparkles, Heart, Star, Package, Flame, Percent } from 'lucide-react';
import { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Product, isDiscountActive, getDiscountedPrice } from '@/types/product';
import { useCart } from '@/context/CartContext';
import { useCartAnimation } from '@/context/CartAnimationContext';
import { useFavorites } from '@/context/FavoritesContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useProductRating } from '@/components/ProductReviews';
import { useProductOrderCount } from '@/hooks/useProductOrderCount';
import { cn } from '@/lib/utils';
import { getProxiedImageUrl } from '@/utils/imageProxy';

const weightBasedUnits = ['кг', 'kg', 'г', 'g'];

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const navigate = useNavigate();
  const { addToCart, addToCartWithCustomUnit } = useCart();
  const { triggerFlyAnimation } = useCartAnimation();
  const { isFavorite, toggleFavorite } = useFavorites();
  
  const [added, setAdded] = useState(false);
  const [showWeightPicker, setShowWeightPicker] = useState(false);
  const [customWeight, setCustomWeight] = useState('');
  const [useOriginalImg, setUseOriginalImg] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const rating = useProductRating(product.id);
  const { orderCount, isBestseller: isBestsellerByOrders } = useProductOrderCount(product.id);
  
  // Проксируем URL изображения для скрытия прямых ссылок на Supabase
  const proxiedImageUrl = useMemo(() => getProxiedImageUrl(product.image_url), [product.image_url]);
  const displayImageUrl = useOriginalImg ? product.image_url : proxiedImageUrl;
  
  // Показываем бейдж "Хит продаж" если установлен вручную ИЛИ много заказов
  const showBestsellerBadge = product.is_bestseller || isBestsellerByOrders;
  
  const isWeightBased = weightBasedUnits.includes(product.unit.toLowerCase());
  const isPackage = product.unit === 'уп';
  
  const baseQuantity = product.base_quantity || 1;
  const hasCustomBaseQuantity = baseQuantity !== 1;
  
  // Проверяем активность акции и рассчитываем цены
  const hasActiveDiscount = isDiscountActive(product);
  const originalPrice = isPackage ? product.price : Math.round(product.price * baseQuantity);
  const discountedBasePrice = hasActiveDiscount ? getDiscountedPrice(product) : product.price;
  const displayPrice = isPackage ? discountedBasePrice : Math.round(discountedBasePrice * baseQuantity);
  
  const pricePerItem = isPackage && baseQuantity > 1 
    ? Math.round(discountedBasePrice / baseQuantity) 
    : null;

  const handleAddToCart = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    
    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      triggerFlyAnimation(displayImageUrl || product.image_url, rect.left, rect.top);
    }
    
    if (isWeightBased) {
      const weightToAdd = hasCustomBaseQuantity ? baseQuantity : 1;
      const customUnit = `${weightToAdd} ${product.unit}`;
      addToCartWithCustomUnit(product, 1, customUnit);
    } else {
      addToCart(product);
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const handleAddWithCustomWeight = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    
    const normalizedWeight = customWeight.replace(',', '.');
    const weight = parseFloat(normalizedWeight);
    if (isNaN(weight) || weight <= 0) return;

    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      triggerFlyAnimation(displayImageUrl || product.image_url, rect.left, rect.top);
    }

    const customUnit = `${weight} ${product.unit}`;
    addToCartWithCustomUnit(product, 1, customUnit);
    setAdded(true);
    setShowWeightPicker(false);
    setCustomWeight('');
    setTimeout(() => setAdded(false), 1500);
  };

  const handleQuickWeight = (weight: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    
    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      triggerFlyAnimation(displayImageUrl || product.image_url, rect.left, rect.top);
    }

    const customUnit = `${weight} ${product.unit}`;
    addToCartWithCustomUnit(product, 1, customUnit);
    setAdded(true);
    setShowWeightPicker(false);
    setTimeout(() => setAdded(false), 1500);
  };

  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };

  return (
    <motion.div 
      className="product-card group cursor-pointer h-full flex flex-col bg-card border border-border/50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-300"
      whileHover={{ y: -6, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      onClick={handleCardClick}
    >
        {/* Image Section */}
        <div 
          className="relative overflow-hidden aspect-square bg-gradient-to-br from-muted/20 to-muted/5"
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          style={{ WebkitTouchCallout: 'none', userSelect: 'none' }}
        >
          {displayImageUrl ? (
            <>
              <img
                ref={imageRef}
                src={displayImageUrl}
                alt={product.name}
                loading="lazy"
                decoding="async"
                crossOrigin="anonymous"
                draggable={false}
                onError={() => {
                  if (!useOriginalImg && proxiedImageUrl && product.image_url) {
                    setUseOriginalImg(true);
                  }
                }}
                className="w-full h-full object-cover transition-all duration-300 pointer-events-none"
                style={{ userSelect: 'none', WebkitUserDrag: 'none' } as React.CSSProperties}
              />
              {/* Watermark overlay */}
              <div 
                className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden"
                style={{ 
                  background: `repeating-linear-gradient(
                    -45deg,
                    transparent,
                    transparent 60px,
                    rgba(0,0,0,0.02) 60px,
                    rgba(0,0,0,0.02) 120px
                  )`
                }}
              >
                <span 
                  className="absolute text-[8px] sm:text-[10px] font-bold text-black/10 rotate-[-30deg] whitespace-nowrap select-none"
                  style={{ textShadow: '0 0 1px rgba(255,255,255,0.3)' }}
                >
                  FERMERCHEL74.RU
                </span>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center px-4 space-y-1">
                <div className="w-12 h-12 mx-auto rounded-full bg-muted/30 flex items-center justify-center mb-2">
                  <Package className="w-5 h-5 text-muted-foreground/40" />
                </div>
                <p className="text-muted-foreground/50 text-[10px] sm:text-xs font-semibold uppercase tracking-wider">
                  Скоро фото
                </p>
              </div>
            </div>
          )}
          
          {/* Gradient overlay for better badge visibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
          
          {/* Badges */}
          <div className="absolute top-2 left-2 md:top-3 md:left-3 flex flex-col gap-1.5">
            {hasActiveDiscount && (
              <motion.span 
                className="inline-flex items-center gap-1 px-2 py-0.5 md:px-2.5 md:py-1 rounded-lg text-[10px] md:text-xs font-bold bg-destructive text-destructive-foreground shadow-lg"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Percent className="w-2.5 h-2.5 md:w-3 md:h-3" />
                −{product.discount_percent}%
              </motion.span>
            )}
            {showBestsellerBadge && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 md:px-2.5 md:py-1 rounded-lg text-[10px] md:text-xs font-bold bg-warning text-warning-foreground shadow-lg">
                <Flame className="w-2.5 h-2.5 md:w-3 md:h-3" />
                Хит
              </span>
            )}
            {product.is_new && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 md:px-2.5 md:py-1 rounded-lg text-[10px] md:text-xs font-bold bg-info text-info-foreground shadow-lg">
                <Sparkles className="w-2.5 h-2.5 md:w-3 md:h-3" />
                New
              </span>
            )}
          </div>
          
          {/* Category badge - top left, below other badges */}
          <div className="absolute bottom-2 left-2 md:bottom-3 md:left-3">
            <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-1 rounded-lg text-[10px] md:text-xs font-medium backdrop-blur-md shadow-sm bg-black/70 text-white border border-white/10">
              {product.category}
            </span>
          </div>
          
          {/* Favorite Button */}
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleFavorite(product);
            }}
            className={cn(
              "absolute top-2 right-2 md:top-3 md:right-3 p-1.5 md:p-2 rounded-full transition-colors duration-200 backdrop-blur-sm",
              isFavorite(product.id)
                ? "bg-primary text-primary-foreground"
                : "bg-background/80 text-muted-foreground hover:bg-background hover:text-primary"
            )}
            whileTap={{ scale: 0.85 }}
          >
            <motion.div
              key={isFavorite(product.id) ? 'filled' : 'empty'}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Heart className={cn("w-3.5 h-3.5 md:w-4 md:h-4", isFavorite(product.id) && "fill-current")} />
            </motion.div>
          </motion.button>
          
          {/* Out of Stock */}
          {!product.in_stock && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center">
              <span className="text-muted-foreground font-semibold text-xs md:text-sm">Нет в наличии</span>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-3 md:p-4 flex flex-col flex-1 bg-gradient-to-b from-transparent to-muted/5">
          <h3 className="font-display text-sm md:text-base font-semibold mb-1.5 line-clamp-2 min-h-[2.5rem] md:min-h-[3rem] text-foreground group-hover:text-primary transition-colors duration-300">
            {product.name}
          </h3>
          
          {/* Rating and Order Count */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {rating && rating.count > 0 && (
              <div className="flex items-center gap-1 bg-muted/50 rounded-full px-2 py-0.5">
                <Star className="w-3 h-3 text-warning fill-warning" />
                <span className="text-[10px] md:text-xs font-medium">{rating.average.toFixed(1)}</span>
                <span className="text-[10px] md:text-xs text-muted-foreground">({rating.count})</span>
              </div>
            )}
            {orderCount !== null && orderCount > 0 && (
              <div className="flex items-center gap-1 text-[10px] md:text-xs text-muted-foreground bg-muted/30 rounded-full px-2 py-0.5">
                <Package className="w-3 h-3" />
                <span>{orderCount}+</span>
              </div>
            )}
          </div>
          
          <p className="text-muted-foreground text-[10px] md:text-xs mb-2 md:mb-3 line-clamp-2 flex-1 leading-relaxed">
            {product.description}
          </p>

          {/* Price and Actions */}
          <div className="flex items-end justify-between gap-2 mt-auto">
            <div className="min-w-0">
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className={cn(
                  "price-tag text-base md:text-lg lg:text-xl",
                  hasActiveDiscount && "text-destructive"
                )}>
                  {displayPrice} ₽
                </span>
                {hasActiveDiscount && (
                  <span className="text-muted-foreground text-[10px] md:text-xs line-through">
                    {originalPrice} ₽
                  </span>
                )}
              </div>
              {isPackage ? (
                <>
                  <span className="text-muted-foreground text-[10px] md:text-xs">/ уп</span>
                  {pricePerItem && (
                    <span className="text-muted-foreground text-[9px] md:text-[10px] block">({pricePerItem} ₽/шт)</span>
                  )}
                </>
              ) : hasCustomBaseQuantity ? (
                <span className="text-muted-foreground text-[10px] md:text-xs">/ ~{baseQuantity} {product.unit}</span>
              ) : (
                <span className="text-muted-foreground text-[10px] md:text-xs">/ {product.unit}</span>
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Weight picker */}
              {isWeightBased && product.in_stock && (
                <Popover open={showWeightPicker} onOpenChange={setShowWeightPicker}>
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 md:h-9 md:w-9 p-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Scale className="h-3.5 w-3.5 md:h-4 md:w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-52 p-3 bg-popover border-border" 
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="space-y-3">
                      <div className="text-sm font-medium">Выбрать вес</div>
                      <div className="flex flex-wrap gap-1.5">
                        {hasCustomBaseQuantity && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="text-xs px-2 py-1 h-7 font-medium"
                            onClick={(e) => handleQuickWeight(baseQuantity, e)}
                          >
                            ~{baseQuantity} {product.unit}
                          </Button>
                        )}
                        {[0.3, 0.5, 0.7, 1, 1.5, 2].filter(w => w !== baseQuantity).map((w) => (
                          <Button
                            key={w}
                            size="sm"
                            variant="outline"
                            className="text-xs px-2 py-1 h-7"
                            onClick={(e) => handleQuickWeight(w, e)}
                          >
                            {w} {product.unit}
                          </Button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder="Свой вес"
                          value={customWeight}
                          onChange={(e) => setCustomWeight(e.target.value)}
                          className="h-8 text-sm"
                          step="0.1"
                          min="0.1"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Button
                          size="sm"
                          className="h-8 shrink-0"
                          onClick={handleAddWithCustomWeight}
                          disabled={!customWeight || parseFloat(customWeight) <= 0}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {/* Add to cart button */}
              <motion.div
                animate={added ? { scale: [1, 1.15, 1] } : {}}
                transition={{ duration: 0.3 }}
              >
                <Button
                  size="sm"
                  className="h-8 w-8 md:h-9 md:w-9 p-0"
                  onClick={handleAddToCart}
                  disabled={!product.in_stock}
                >
                  <AnimatePresence mode="wait">
                    {added ? (
                      <motion.div
                        key="check"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Check className="h-3.5 w-3.5 md:h-4 md:w-4" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="cart"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ShoppingCart className="h-3.5 w-3.5 md:h-4 md:w-4" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
  );
};

export default ProductCard;
